
import React, { useState, useEffect, useMemo, useCallback,useRef } from "react";
import { Box, Flex, Heading, Text, Spinner, Button } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./Styles/StoreAbstractGrid.css";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

function StoreAbstract() {
  const isFirstRender = useRef(true);
  const getCurrentDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  };
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  };

  // ── Display states (what user sees in input) ──
  const [fromDate, setFromDate] = useState(getFirstDayOfMonth);
  const [toDate, setToDate]     = useState(getCurrentDate());

  // ✅ committedDates — API call only fires when THIS changes
  const [committedDates, setCommittedDates] = useState({
    from: getFirstDayOfMonth(),
    to:   getCurrentDate(),
  });

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [rowData, setRowData]   = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);
  const [currentPage, setCurrentPage]     = useState(0);
  const [totalPages, setTotalPages]       = useState(1);
  const [pageSize]                        = useState(10);
  const [gridApi, setGridApi]             = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  
  // ── Date handlers — only update display, NOT fetch ──
  const handleFromDateChange = (v) => {
    setFromDate(v);
    if (v > toDate) setToDate(v);
  };
  const handleToDateChange = (v) => {
    if (v < fromDate) return;
    setToDate(v);
  };

 useEffect(() => {
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return; // skip on first mount — prevents duplicate fetch
  }
  const timer = setTimeout(() => {
    setCurrentPage(0);
    setCommittedDates({ from: fromDate, to: toDate });
  }, 800);
  return () => clearTimeout(timer);
}, [fromDate, toDate]);

  // ── Pagination ──
 const handlePrevious = () => {
  if (currentPage > 0) setCurrentPage((p) => p - 1);
};
const handleNext = () => {
  if (currentPage < totalPages - 1) setCurrentPage((p) => p + 1);
};

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    filterParams: { maxNumConditions: 1, buttons: ["reset"] },
    resizable: true,
    minWidth: 120,
    suppressHeaderMenuButton: false,
    menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  const colDef = (headerName, field, filter = "agTextColumnFilter", extra = {}) => ({
    headerName, field, filter, ...extra,
  });

  const columnDefs = useMemo(() => [
    colDef("Outlet Code",        "outletCode",        "agTextColumnFilter",   { minWidth: 140 }),
    colDef("Store Name",         "storeName",         "agTextColumnFilter",   { minWidth: 160 }),
    colDef("Store Type",         "storeType",         "agTextColumnFilter",   { minWidth: 80, width: 120 }),
    colDef("Total Score",        "totalScore",        "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" } }),
    colDef("Availability Score", "avbScr",            "agNumberColumnFilter", { minWidth: 80, width: 130, cellStyle: { textAlign: "right" } }),
    colDef("Activation Score",   "actScr",            "agNumberColumnFilter", { minWidth: 80, width: 120, cellStyle: { textAlign: "right" } }),
    colDef("Total Cooler Score", "totalCoolerScr",    "agNumberColumnFilter", { minWidth: 80, width: 130, cellStyle: { textAlign: "right" } }),
    colDef("Total Purity %",     "totalPurityPct",    "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("No. of Shelves",     "numberOfShelves",   "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" } }),
    colDef("Pure Shelf 1 %",     "purShelf1Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 2 %",     "purShelf2Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 3 %",     "purShelf3Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 4 %",     "purShelf4Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 5 %",     "purShelf5Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 6 %",     "purShelf6Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 7 %",     "purShelf7Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 8 %",     "purShelf8Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 9 %",     "purShelf9Pct",      "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
    colDef("Pure Shelf 10 %",    "purShelf10Pct",     "agNumberColumnFilter", { minWidth: 80, width: 110, cellStyle: { textAlign: "right" }, valueFormatter: (p) => p.value != null ? `${p.value}` : "" }),
  ], []);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const onFilterChanged = useCallback((params) => {
    if (!params?.api) return;
    const filterModel = params.api.getFilterModel();
    const isFilterActive = Object.keys(filterModel).length > 0;
    if (isFilterActive) {
    setFilteredCount(params.api.getDisplayedRowCount());
    } else {
    setFilteredCount(totalElements);
    }
}, [totalElements]);


 
  const parseDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return { year, month, day };
  };

  
  const fetchData = useCallback(async () => {
    if (!committedDates.from || !committedDates.to) return;

    const from = parseDate(committedDates.from);
    const to   = parseDate(committedDates.to);

    setLoading(true);
    setError(null);
    setRowData([]);

    const requestBody = {
      from: { date: from.day, month: from.month, year: from.year },
      to:   { date: to.day,   month: to.month,   year: to.year   },
    };

    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.STORE_ABSTRACT.REPORT}?page=${currentPage}&size=${pageSize}`,
        requestBody
      );
      const content = res.data?.content ?? []; 
      const mapped = content.map((item) => ({
        outletCode:      item.storeid            || "",
        storeName:       item.storeName          || "",
        storeType:       item.storeType          || "",
        totalScore:      item.totalScore         || 0,
        avbScr:          item.availabilityScore  || 0,
        actScr:          item.activationScore    || 0,
        totalCoolerScr:  item.coolerScore        || 0,
        totalPurityPct:  item.totalPurity        || 0,
        numberOfShelves: item.totalNoOfShelves   || 0,
        purShelf1Pct:    item.shelf1Pur          || 0,
        purShelf2Pct:    item.shelf2Pur          || 0,
        purShelf3Pct:    item.shelf3Pur          || 0,
        purShelf4Pct:    item.shelf4Pur          || 0,
        purShelf5Pct:    item.shelf5Pur          || 0,
        purShelf6Pct:    item.shelf6Pur          || 0,
        purShelf7Pct:    item.shelf7Pur          || 0,
        purShelf8Pct:    item.shelf8Pur          || 0,
        purShelf9Pct:    item.shelf9Pur          || 0,
        purShelf10Pct:   item.shelf10Pur         || 0,
      }));
      setRowData(mapped);
      setTotalPages(res.data?.totalPages ?? 1);        
      setTotalElements(res.data?.totalElements ?? 0);
    } catch (err) {
      console.error("Error fetching store abstract data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [committedDates,currentPage]); 

  useEffect(() => { fetchData(); }, [fetchData]);

 useEffect(() => {
  setFilteredCount(totalElements);
}, [totalElements, currentPage]);

  // ── Excel Download ──
  const handleDownloadExcel = () => {
    setDownloadLoading(true);
    try {
      const exportData = [];
      if (gridApi) {
        gridApi.forEachNodeAfterFilter((node) => exportData.push(node.data));
      }
      const dataToExport = exportData.length > 0 ? exportData : rowData;

      const HEADERS = [
        "Outlet Code", "Store Name", "Store Type",
        "Total Score", "Availability Score", "Activation Score",
        "Total Cooler Score", "Total Purity %", "No. of Shelves",
        "Pure Shelf 1 %", "Pure Shelf 2 %", "Pure Shelf 3 %",
        "Pure Shelf 4 %", "Pure Shelf 5 %", "Pure Shelf 6 %",
        "Pure Shelf 7 %", "Pure Shelf 8 %", "Pure Shelf 9 %",
        "Pure Shelf 10 %",
      ];
      const FIELDS = [
        "outletCode", "storeName", "storeType",
        "totalScore", "avbScr", "actScr",
        "totalCoolerScr", "totalPurityPct", "numberOfShelves",
        "purShelf1Pct", "purShelf2Pct", "purShelf3Pct",
        "purShelf4Pct", "purShelf5Pct", "purShelf6Pct",
        "purShelf7Pct", "purShelf8Pct", "purShelf9Pct",
        "purShelf10Pct",
      ];

      const headerStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top:    { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left:   { style: "thin", color: { rgb: "000000" } },
          right:  { style: "thin", color: { rgb: "000000" } },
        },
      };
      const dataStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
        font: { color: { rgb: "2D3748" }, sz: 11 },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top:    { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left:   { style: "thin", color: { rgb: "000000" } },
          right:  { style: "thin", color: { rgb: "000000" } },
        },
      };

      const wsData = [
        HEADERS.map((h) => ({ v: h, t: "s", s: headerStyle })),
        ...dataToExport.map((row) =>
          FIELDS.map((f) => ({
            v: row[f] !== undefined && row[f] !== null ? String(row[f]) : "",
            t: "s",
            s: dataStyle,
          }))
        ),
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = HEADERS.map(() => ({ wch: 20 }));
      ws["!rows"] = [{ hpt: 30 }, ...dataToExport.map(() => ({ hpt: 22 }))];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Store Abstract Report");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Store_Abstract_Report_${committedDates.from}_to_${committedDates.to}.xlsx`
      );
    } catch (err) {
      console.error("Excel download failed:", err);
      alert("Failed to download Excel. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const [verifyFilter, setVerifyFilter] = useState("total");
 
  const filteredRowData = useMemo(() => {
  if (verifyFilter === "total") return rowData;
  if (verifyFilter === "verified")   return rowData.filter((r) => r.isVerified === true);
  if (verifyFilter === "unverified") return rowData.filter((r) => r.isVerified === false);
  return rowData;
}, [rowData, verifyFilter]);

  return (
    <Box minH="100vh" bg="gray.50" pt={{ base: "10vh", md: "13vh" }}>

      {/* ── Title + Date Filters ── */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        gap={4} mb={6} px={6}
      >
        <Box>
          <Heading size="lg" color="gray.800" mb={2}>Store Abstract Report</Heading>
          <Text color="gray.500">Drill into store performance metrics and trends</Text>
        </Box>

        <Flex alignItems="center" gap={4} flexWrap="wrap">
          {[
            { label: "From Date", value: fromDate, max: toDate,   onChange: handleFromDateChange },
            { label: "To Date",   value: toDate,   min: fromDate, onChange: handleToDateChange  },
          ].map(({ label, value, min, max, onChange }) => (
            <Box key={label} minW="210px">
              <Text fontSize="md" color="gray.800" fontWeight="bold" mb={1} mx={2}>{label}</Text>
              <Box
                as="input" type="date" value={value} min={min} max={max}
                onChange={(e) => onChange(e.target.value)}
                sx={{
                  width: "100%", padding: "8px 12px", borderRadius: "8px",
                  border: "1px solid", borderColor: "gray.300", bg: "white",
                  fontSize: "16px", cursor: "pointer",
                  "&:focus": { outline: "none", borderColor: "red.500", boxShadow: "0 0 0 3px rgba(244,0,9,0.3)" },
                }}
              />
            </Box>
          ))}
        </Flex>
      </Flex>

      {/* ── Record Count + Download ── */}
      <Flex
        align="center"
        justify="space-between"
        mb={{ base: 3, md: 4 }}
        px={6}
        flexWrap="wrap"
        gap={3}
      >
         <Flex align="center" gap={4} flexWrap="wrap">
        <Flex
          align="center" gap={1.5}
          bg="gray.700"
          px={{ base: 3, md: 4 }} py={{ base: 1, md: 1.5 }}
          borderRadius="full"
          boxShadow="0 2px 4px rgba(0,0,0,0.2)"
        >
          <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} fontWeight="bold" color="white">
            {filteredCount < totalElements ? "Filtered:" : "Total:"}
          </Text>
          <Text color="white" fontSize={{ base: "sm", sm: "md", md: "lg" }} fontWeight="bold">
            {filteredCount} Records
          </Text>
        </Flex>

            {/* Radio Buttons */}
       <Flex align="center" gap={4}>
        {["total", "verified", "unverified"].map((option) => (
        <Flex
          key={option}
          align="center"
          gap={1.5}
          cursor="pointer"
          onClick={() => setVerifyFilter(option)}
        >
          <Box
            w="18px" h="18px"
            borderRadius="full"
            border="2px solid"
            borderColor={verifyFilter === option ? "red.500" : "gray.400"}
            display="flex" alignItems="center" justifyContent="center"
            transition="all 0.2s"
          >
            {verifyFilter === option && (
              <Box
                w="9px" h="9px"
                borderRadius="full"
                bg="red.500"
              />
            )}
          </Box>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            fontWeight={verifyFilter === option ? "bold" : "medium"}
            color={verifyFilter === option ? "red.500" : "gray.800"}
            textTransform="capitalize"
            userSelect="none"
          >
            {option}
          </Text>
        </Flex>
      ))}
      </Flex>
    </Flex>


        <Button
          colorScheme="green"
          size={{ base: "sm", md: "md" }}
          onClick={handleDownloadExcel}
          isLoading={downloadLoading}
          loadingText="Downloading..."
          spinner={<Spinner size="sm" color="white" />}
          rightIcon={!downloadLoading ? <Box as="span" fontSize={{ base: "14px", md: "18px" }}>⬇</Box> : null}
          w={{ base: "100%", sm: "auto" }}
          h={{ base: "32px", sm: "38px", md: "42px" }}
          px={{ base: 6, md: 9 }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          fontWeight="bold"
          boxShadow="0 2px 4px rgba(33,199,74,0.3)"
          _hover={{
            transform: downloadLoading ? "none" : "translateY(-2px)",
            boxShadow: downloadLoading ? "0 2px 4px rgba(33,199,74,0.3)" : "0 4px 8px rgba(33,199,74,0.4)",
          }}
          transition="all 0.2s"
          isDisabled={downloadLoading || rowData.length === 0}
        >
          Download Excel
        </Button>
      </Flex>

      {/* ── AG Grid ── */}
      <Box px={3} pb={8}>
        <Box className="ag-theme-alpine" style={{ height: "560px", width: "100%" }}>
          {loading ? (
            <Flex justify="center" align="center" h="100%">
              <Spinner size={{ base: "xl", md: "2xl" }} color="red.500" thickness="15px" />
            </Flex>
          ) : (
            <AgGridReact
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
              animateRows={true}
              rowHeight={window.innerWidth < 768 ? 60 : 64}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressRowClickSelection={true}
              overlayNoRowsTemplate={
                `<span style="color:#9ca3af;font-size:15px;font-weight:500;">
                  No data available. Please load data to view results.
                </span>`
              }
            />
          )}
        </Box>
      </Box>

      {/* ── Pagination ── */}
      <Flex
        justify="space-between" align="center"
        mt={4} px={{ base: 2, md: 4 }}
        flexDirection={{ base: "column", sm: "row" }}
        gap={{ base: 3, sm: 0 }}
      >
        <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
       Showing {totalElements > 0 ? currentPage * pageSize + 1 : 0} to{" "}
       {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} records
        </Text>

        <Flex gap={2} align="center">
          <Button
            onClick={handlePrevious}
            isDisabled={currentPage === 0}
            size="sm"
            fontSize={{ base: "md", md: "lg" }}
            variant="outline"
            colorScheme="gray"
            leftIcon={<ChevronLeftIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Previous
          </Button>

          <Flex
            align="center" justify="center"
            bg="red.500" color="white"
            w={{ base: "32px", md: "40px" }}
            h={{ base: "32px", md: "40px" }}
            borderRadius="md" fontWeight="bold"
            fontSize={{ base: "sm", md: "md" }}
            boxShadow="0 2px 4px rgba(199,33,39,0.3)"
          >
            {currentPage+1}
          </Flex>

          <Button
            onClick={handleNext}
            isDisabled={currentPage >= totalPages-1}
            size="sm"
            fontSize={{ base: "md", md: "lg" }}
            variant="outline"
            colorScheme="gray"
            rightIcon={<ChevronRightIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Next
          </Button>
        </Flex>
      </Flex>

    </Box>
  );
}

export default StoreAbstract;